/**
 * Auth abstraction layer.
 *
 * When VITE_AUTH_STUB=true, provides stub implementations so the app
 * can run fully locally without Clerk. Otherwise re-exports from @clerk/react.
 */
import type { PropsWithChildren } from 'react';
import * as clerk from '@clerk/react';

const AUTH_STUB = import.meta.env.VITE_AUTH_STUB === 'true';

/** True when authentication is available (real Clerk or stub). */
export const AUTH_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || AUTH_STUB;

// ── Stub implementations ───────────────────────────────────────────────────

// Module-level constant so the reference never changes between renders,
// preventing infinite useCallback/useEffect cycles in consuming hooks.
const stubGetToken = async () => 'stub-token';

function useAuthStub() {
  return {
    isSignedIn: true as const,
    getToken: stubGetToken,
    userId: 'stub-user-123',
  };
}

function useUserStub() {
  return {
    user: {
      primaryEmailAddress: { emailAddress: 'dev@test.local' },
      fullName: 'Dev User',
      firstName: 'Dev',
    },
  };
}

function StubUserButton() {
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: '#ea580c',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 700,
      }}
      title="Dev User (stub)"
    >
      D
    </div>
  );
}

function StubSignInButton({ children }: PropsWithChildren) {
  return <>{children}</>;
}

function StubClerkProvider({ children }: PropsWithChildren<{ publishableKey?: string }>) {
  return <>{children}</>;
}

// ── Exports: real or stub based on env ─────────────────────────────────────

export const useAuth = AUTH_STUB ? useAuthStub : clerk.useAuth as unknown as typeof useAuthStub;
export const useUser = AUTH_STUB ? useUserStub : clerk.useUser as unknown as typeof useUserStub;
export const UserButton = AUTH_STUB ? StubUserButton : clerk.UserButton;
export const SignInButton = AUTH_STUB ? StubSignInButton : clerk.SignInButton as unknown as typeof StubSignInButton;
export const ClerkProvider = AUTH_STUB ? StubClerkProvider : clerk.ClerkProvider as unknown as typeof StubClerkProvider;
